import debounce from "lodash/debounce"
import { loadPage, observeOnce } from "./utils"
import VanillaFittty from "shared/scripts/vanilla_fittty"
import { addEventListenerWithKey } from "shared/lib/utils.js"
import { onBFCacheLoad } from "shared/scripts/back_forward_cache_utils"
import {
  saveUserSearch,
  createSearchItemFromElement,
  SearchEntryInterface,
} from "app/components/universal_search/search_history"
import { RecentSearches } from "app/components/universal_search/recent_searches"
import { SearchHelperNavigation } from "app/components/universal_search/search_helper_navigation"
import { trackSearchPerformed } from "app/utils/mixpanel_tracking"

const SEARCH_INPUT_DEBOUNCE_MS = 400

/**
 * Mirrors what a direct visit to the same URL renders, so the tab, history entry and
 * RUM view name all say "search" rather than keeping the Explore title. The URL carries
 * the lowercased term, and ExploreController#index builds the title from that term with
 * Ruby's `capitalize`; ApplicationHelper#create_full_page_title adds the " - IFTTT" suffix.
 */
export const searchPageTitle = (term: string) => {
  const lowerTerm = term.toLowerCase()
  // Ruby's capitalize titlecases the first character; JS only has toUpperCase, which
  // uppercases it. The two disagree whenever uppercasing yields more than one character:
  // "ß" uppercases to "SS" but titlecases to "Ss", and the same holds for ligatures such
  // as "ﬁ" -> "Fi". Taking the first letter of the uppercased form and lowercasing the
  // rest reproduces the titlecase for those. It still differs for the handful of Unicode
  // digraphs that have a dedicated titlecase character ("ǳ" -> Ruby "ǲ", here "Ǳ"), which
  // no search term realistically starts with.
  const upperFirst = lowerTerm.charAt(0).toUpperCase()
  const titleFirst = upperFirst.charAt(0) + upperFirst.slice(1).toLowerCase()
  return `Search for '${titleFirst}${lowerTerm.slice(1)}' - IFTTT`
}

export const Search = () => {
  const { asyncSearchPath, currentView, loadingImagePath, moreAppletsForSearchPath, searchPath } =
    document.querySelector(".explore-discover-container").dataset

  const searchContainer = document.querySelector("#explore-search")
  const searchResultContainer = document.querySelector(".search-view-container")
  const searchInput = searchContainer.querySelector("input")
  const searchOffset = 32
  const minimumAppletsToLoad = 100

  let currentOffset = 32
  let historyAction = currentView !== "search" ? "pushState" : "replaceState"

  // Bumped whenever the search the user is waiting for stops being the current one, so an
  // in-flight response cannot paint itself over whatever replaced it. Back out of a search
  // and a request that resolves afterwards would otherwise refill the emptied container.
  let searchGeneration = 0

  // The pathname the popstate handler last acted on. popstate also fires for fragment
  // navigation, so without this a Back from an in-page #anchor on a search URL re-enters the
  // fetch -- swapping the results for the loading placeholder and re-running the same query,
  // losing scroll position. "Nothing to do" has to be a possible answer. Kept in step with
  // URLs this code writes itself, or a search would leave it stale on the Explore path and
  // the genuine Back would be swallowed as a no-op.
  let lastHandledPathname = window.location.pathname

  // The revert is tab_management's, not this file's -- its exploreCleanSearch listener calls
  // activateTab -> replacePage, which rewrites document.title and og:title from the active
  // tab's data-title. explore/index.ts runs TabManagement() before Search() unconditionally,
  // and both require markup that exists only in explore/index.html.erb, so there is no page
  // where one runs without the other. This file owns the forward write only.
  const ogTitleTag = document.querySelector('meta[property="og:title"]') as HTMLMetaElement | null

  // fromHistory marks a search the browser navigated to, not one the user just performed:
  // it must not be written to history again (the entry already exists, and writing would
  // fight the traversal), must not be recorded as a fresh recent search, and must not be
  // reported to Mixpanel -- holding Back/Forward over a search entry would otherwise inflate
  // search volume and the result-count distributions by however often users traverse.
  const fetchSearchResult = (searchEntry: SearchEntryInterface, { fromHistory = false } = {}) => {
    const generation = ++searchGeneration
    if (!fromHistory) saveUserSearch(searchEntry)

    document.removeEventListener("scroll", fetchSearchResultMoreApplets)

    //On the Search page we want the url bar to change to match the query and active tab.
    const parsedTerm = encodeURIComponent(searchEntry.term.toLowerCase())
    const url = `${searchPath}${parsedTerm}`
    if (!fromHistory) {
      window.history[historyAction]({ url }, "", url)
      historyAction = "replaceState"
    }
    lastHandledPathname = url

    // After the history write, not before: a browser records the outgoing entry under the
    // document title at the moment you navigate away from it, so setting the search title
    // first can label the Explore entry in the history UI with the search.
    const title = searchPageTitle(searchEntry.term)
    document.title = title
    if (ogTitleTag) ogTitleTag.content = title

    loadPage(`${asyncSearchPath}${parsedTerm}`).then(html => {
      if (generation !== searchGeneration) return

      currentOffset = searchOffset

      searchResultContainer.innerHTML = html
      void VanillaFittty(searchResultContainer as HTMLElement)

      if (!fromHistory) fireSearchPerformed(searchEntry.term)

      document.addEventListener("scroll", fetchSearchResultMoreApplets, { once: true })
    })
  }

  const fireSearchPerformed = (term: string) => {
    const appletResultCount = searchResultContainer.querySelectorAll("[data-type='applet']").length
    const serviceResultCount = searchResultContainer.querySelectorAll("[data-type='service']").length
    trackSearchPerformed({
      term,
      appletResultCount,
      serviceResultCount,
    })
  }

  const fetchSearchResultMoreApplets = () => {
    const appletSearchContainer = document.querySelector(".explore-search")
    if (!appletSearchContainer) return

    const element = appletSearchContainer.querySelector(".explore-tile[data-sentinel='true']")
    if (!element) return

    element.dataset.sentinel = "false"

    observeOnce(element, () => {
      const generation = searchGeneration
      const searchMorePath = `${moreAppletsForSearchPath}/${encodeURIComponent(searchInput.value)}/${currentOffset}`
      const appletsFromSearch = appletSearchContainer.querySelectorAll(".explore-tile").length
      currentOffset += searchOffset

      // After achieve the minimumAppletsToLoad, don't need to ask for more
      if (appletsFromSearch < minimumAppletsToLoad) {
        loadPage(searchMorePath).then(html => {
          // The container this page belongs to is detached once the search is cleared.
          if (generation !== searchGeneration) return

          appletSearchContainer.insertAdjacentHTML("beforeend", html)
          void VanillaFittty(searchResultContainer as HTMLElement)
          document.addEventListener("scroll", fetchSearchResultMoreApplets, { once: true })
          window.App.Utils.logCustomDatadogAction("explore_search_completed", {
            searchTerm: searchInput.value.toLowerCase(),
          })
        })
      }
    })
  }

  const cleanSearch = () => {
    searchGeneration += 1
    debouncedTypingSearch.cancel()
    searchResultContainer.classList.add("hide")
    searchResultContainer.innerHTML = ""

    // This dispatch is what restores the title: tab_management listens for it.
    window.dispatchEvent(new CustomEvent("exploreCleanSearch", {}))
  }

  addEventListenerWithKey(searchInput, "focus", "explore_search_focus", () => {
    window.App.Utils.logCustomDatadogAction("explore_click_search_bar")
  })

  /** Show search chrome then fetch (used after typing stabilizes or from dropdown clicks). */
  const commitSearchUiAndFetch = (searchEntry: SearchEntryInterface, { fromHistory = false } = {}) => {
    window.dispatchEvent(new CustomEvent("exploreSearching", {}))

    searchResultContainer.innerHTML = `<div class="explore-search-loading-placeholder"><img class="loading" src="${loadingImagePath}" width="150" height="343" alt="" /></div>`
    searchResultContainer?.classList.remove("hide")

    if (!searchEntry.term || searchEntry.term.trim().length === 0) {
      cleanSearch()
    } else {
      fetchSearchResult(searchEntry, { fromHistory })
    }
  }

  const debouncedTypingSearch = debounce(commitSearchUiAndFetch, SEARCH_INPUT_DEBOUNCE_MS)

  addEventListenerWithKey(searchInput, "input", "explore_search_input", event => {
    //Not trimming this here because we want the user to be able to put spaces in the search bar.
    const term = (event.target as HTMLInputElement).value

    if (!term || term.trim().length === 0) {
      debouncedTypingSearch.cancel()
      cleanSearch()
      return
    }

    debouncedTypingSearch({ type: "search", term })
  })

  addEventListenerWithKey(
    window,
    `clean-search:${searchContainer.id}`,
    `explore_search_clean_${searchContainer.id}`,
    cleanSearch,
  )

  // Searching pushes a history entry, but nothing put the view back when the user
  // traverses out of it. onBFCacheLoad below does not cover this: it listens for
  // pageshow/persisted, which is a cross-document bfcache restore, whereas Back from a
  // pushState search is a same-document popstate. The result was a page whose URL said
  // /explore while the search results and the typed term were both still on screen.
  //
  // Restore whatever the URL now describes. cleanSearch already does the Explore half --
  // it empties the results and dispatches exploreCleanSearch, which tab_management uses
  // to re-activate the current tab.
  addEventListenerWithKey(window, "popstate", `explore_search_popstate_${searchContainer.id}`, () => {
    const { pathname } = window.location

    if (pathname === lastHandledPathname) return
    lastHandledPathname = pathname

    if (!pathname.startsWith(searchPath)) {
      // Back on Explore, so the next search opens a fresh entry again. Leaving this as
      // "replaceState" would overwrite the Explore entry the user just returned to, and
      // the following Back would leave the page instead of restoring Explore.
      historyAction = "pushState"
      searchInput.value = ""
      cleanSearch()
      return
    }

    // On a search entry, so a later search refines this one in place, matching how the
    // server-rendered search view starts out.
    historyAction = "replaceState"

    // Traversed to a search entry: re-run it so the results match the URL again. The decode
    // raises URIError on a malformed percent-escape, which only an entry this code did not
    // write can carry -- leave the view alone rather than breaking the handler.
    let term
    try {
      term = decodeURIComponent(pathname.slice(searchPath.length))
    } catch {
      return
    }
    if (!term) return

    debouncedTypingSearch.cancel()
    searchInput.value = term
    commitSearchUiAndFetch({ type: "search", term }, { fromHistory: true })
  })

  RecentSearches()
  SearchHelperNavigation(searchContainer)

  // unhide the dropdown, hidden by default to avoid FOUC
  searchContainer.querySelector(".universal-search-helper")?.removeAttribute("style")

  // We want to prevent the anchor behaviour and use our async search via search input type change.
  const dropdownHelperClick = (eventScope: "recent_searches" | "recommended_service") => (event: Event) => {
    event.preventDefault()
    event.stopPropagation()

    const serviceAnchor = (event.target as Element).closest("li > a") as HTMLAnchorElement
    const searchEntry = createSearchItemFromElement(serviceAnchor) as SearchEntryInterface

    searchInput.value = searchEntry.term
    if (eventScope === "recent_searches") {
      window.App.Utils.logCustomDatadogAction(`explore_search_recent_searches_clicked`, { term: searchEntry.term })
    } else {
      window.App.Utils.logCustomDatadogAction(`explore_search_recommended_service_clicked`, {
        service: searchEntry.term,
      })
    }
    debouncedTypingSearch.cancel()
    commitSearchUiAndFetch(searchEntry)
  }

  const recentSearchesWrapper = searchContainer.querySelector(".recent-searches")
  if (recentSearchesWrapper) {
    addEventListenerWithKey(
      recentSearchesWrapper,
      "click",
      "explore_search_recent_searches_click",
      dropdownHelperClick("recent_searches")
    )
  }

  const recommendedServicesWrapper = searchContainer.querySelector(".recommended-services")
  if (recommendedServicesWrapper) {
    addEventListenerWithKey(
      recommendedServicesWrapper,
      "click",
      "explore_search_recommended_services_click",
      dropdownHelperClick("recommended_service")
    )
  }

  if (currentView === "search") {
    document.addEventListener("scroll", fetchSearchResultMoreApplets, { once: true })
    // Fire "Search Performed" on initial page load for server-rendered search
    // (e.g. direct navigation, refresh, bookmark). Async searches fire it via
    // fetchSearchResult.
    fireSearchPerformed(searchInput.value)
  }

  onBFCacheLoad("explore_search", () => {
    Search()
  })
}
